/**
 * usePlan - Hook for plan-based feature access control
 *
 * Returns boolean flags and helpers based on the user's current subscription plan.
 * All plan checks should go through this hook to keep enforcement centralized.
 */
import { useAuth } from './useAuth';

export type PlanId = 'personal' | 'professional' | 'business' | 'enterprise';

const PLAN_RANK: Record<PlanId, number> = {
  personal: 0,
  professional: 1,
  business: 2,
  enterprise: 3,
};

// Mirrors server/config/plan-features.js
const PLAN_FEATURES: Record<PlanId, Record<string, any>> = {
  personal: {
    qrStore: true,
    qrWallet: true,
    appearance: true,
    customQrDesign: false,
    highResQrDownload: false,
    removeBranding: false,
    dynamicQr: false,
    qrAnalytics: false,

    analyticsTimePeriod: '7days',
    exportAnalytics: false,
    demographicsData: false,
    deviceBreakdown: false,

    aiNotes: true,
    speakerDiarization: false,
    aiExtraction: false,

    advancedLeadForms: true,
    leadExport: false,
    leadAnalytics: false,

    teamFeatures: false,
    crmSync: false,

    whiteLabel: false,
    unlimitedApi: false,
    corporateDashboard: false,

    emailCampaigns: false,
    emailDailyLimit: 0,
  },
  professional: {
    qrStore: true,
    qrWallet: true,
    appearance: true,
    customQrDesign: true,
    highResQrDownload: true,
    removeBranding: true,
    dynamicQr: true,
    qrAnalytics: true,

    analyticsTimePeriod: 'all',
    exportAnalytics: true,
    demographicsData: true,
    deviceBreakdown: true,

    aiNotes: true,
    speakerDiarization: true,
    aiExtraction: true,

    advancedLeadForms: true,
    leadExport: true,
    leadAnalytics: true,

    teamFeatures: false,
    crmSync: false,

    whiteLabel: false,
    unlimitedApi: false,
    corporateDashboard: false,

    emailCampaigns: true,
    emailDailyLimit: 50,
  },
  business: {
    qrStore: true,
    qrWallet: true,
    appearance: true,
    customQrDesign: true,
    highResQrDownload: true,
    removeBranding: true,
    dynamicQr: true,
    qrAnalytics: true,

    analyticsTimePeriod: 'all',
    exportAnalytics: true,
    demographicsData: true,
    deviceBreakdown: true,

    aiNotes: true,
    speakerDiarization: true,
    aiExtraction: true,

    advancedLeadForms: true,
    leadExport: true,
    leadAnalytics: true,

    teamFeatures: true,
    crmSync: true,

    whiteLabel: false,
    unlimitedApi: false,
    corporateDashboard: true,

    emailCampaigns: true,
    emailDailyLimit: 120,
  },
  enterprise: {
    qrStore: true,
    qrWallet: true,
    appearance: true,
    customQrDesign: true,
    highResQrDownload: true,
    removeBranding: true,
    dynamicQr: true,
    qrAnalytics: true,

    analyticsTimePeriod: 'all',
    exportAnalytics: true,
    demographicsData: true,
    deviceBreakdown: true,

    aiNotes: true,
    speakerDiarization: true,
    aiExtraction: true,

    advancedLeadForms: true,
    leadExport: true,
    leadAnalytics: true,

    teamFeatures: true,
    crmSync: true,

    whiteLabel: true,
    unlimitedApi: true,
    corporateDashboard: true,

    emailCampaigns: true,
    emailDailyLimit: 300,
  },
};

export function usePlan() {
  const { user } = useAuth();
  const plan = (user?.subscription?.plan ?? 'personal') as PlanId;
  const rank = PLAN_RANK[plan] ?? 0;
  const features = PLAN_FEATURES[plan] ?? PLAN_FEATURES.personal;

  /** Check if the current plan includes a feature */
  const can = (feature: string): boolean => !!features[feature];

  /** Check if the current plan meets a minimum plan tier */
  const isPlanAtLeast = (minPlan: PlanId): boolean => rank >= PLAN_RANK[minPlan];

  // Convenience booleans
  const isPaid = plan !== 'personal';
  const isProfessional = isPlanAtLeast('professional');
  const isBusiness = isPlanAtLeast('business');
  const isEnterprise = plan === 'enterprise';

  // Analytics
  const analyticsTimePeriod: '7days' | '30days' | '90days' | 'all' = features.analyticsTimePeriod;

  return {
    plan,
    rank,
    features,
    can,
    isPlanAtLeast,
    isPaid,
    isProfessional,
    isBusiness,
    isEnterprise,
    // Named feature flags
    analyticsTimePeriod,
    canExportAnalytics: can('exportAnalytics'),
    canViewDemographics: can('demographicsData'),
    canViewDeviceBreakdown: can('deviceBreakdown'),
    canUseAiNotes: can('aiNotes'),
    canUseSpeakerDiarization: can('speakerDiarization'),
    canUseAiExtraction: can('aiExtraction'),
    canUseQrStore: can('qrStore'),
    canUseQrWallet: can('qrWallet'),
    canUseAppearance: can('appearance'),
    canCustomizeQr: can('customQrDesign'),
    canHighResDownload: can('highResQrDownload'),
    canRemoveBranding: can('removeBranding'),
    canUseDynamicQr: can('dynamicQr'),
    canViewQrAnalytics: can('qrAnalytics'),
    canExportLeads: can('leadExport'),
    canViewLeadAnalytics: can('leadAnalytics'),
    canUseTeamFeatures: can('teamFeatures'),
    canUseCrmSync: can('crmSync'),
    canUseWhiteLabel: can('whiteLabel'),
    canUseEmailCampaigns: can('emailCampaigns'),
    emailDailyLimit: (features.emailDailyLimit ?? 0) as number,
  };
}
